import { AfterContentInit, Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Quantity, ArticleDescription, ProductType } from 'chronos-core-client';
import { CounterHelper } from '../../../services';

type onChanged = (value: number) => void;
type onTouched = () => void;

@Component({
  selector: 'lib-pallet-quantity-calculation',
  templateUrl: './pallet-quantity-calculation.component.html',
  styleUrls: ['./pallet-quantity-calculation.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PalletQuantityCalculationComponent),
      multi: true
    }
  ]
})
export class PalletQuantityCalculationComponent implements OnInit, AfterContentInit, ControlValueAccessor {
  @Input() public formUnitLabel = '';
  @Input() public article: ArticleDescription;
  @Input() public bomUnitId = 'SHT';
  @Input() public disabled = false;
  @Input() public bomUnitFactor? = null;
  @Input() public inventoryQuantity: Quantity;
  @Output() public validationChanged = new EventEmitter<boolean>();

  public readonly INPUT_MIN_NUMBER = 0;
  public readonly INPUT_STYLE = {
    width: '110px'
  };

  public formInventoryQuantity = 0;
  public height = 0;
  public bomQuantity = 0;
  public decimalDigitCount = 0;
  public inventoryUnitId: string;
  public thickness = 0;
  public outerCoreDiameter = 0;
  public outerReelDiameter = 0;
  public isHeightDisplay = false;
  public isInventoryUnitDisplayed = false;
  public isReelDiameterDisplay = false;
  public isDiameterInputValid = true;
  private initialValue = 0;
  private propagateChanged: onChanged = () => {};
  private propagateTouched: onTouched = () => {};

  public ngOnInit() {
    this.decimalDigitCount = this.setDecimalDigitCount(this.bomUnitId);
    this.isInventoryUnitDisplayed = this.checkInventoryUnitDisplay();
    this.isHeightDisplay = this.checkHeightDisplay();
    this.isReelDiameterDisplay = this.checkReelDisplay();
    this.inventoryUnitId = this.article?.inventoryUnitId;
    this.thickness = this.article?.thickness;
    this.bomUnitFactor = this.bomUnitFactor ?? this.article?.bomUnitFactor;
  }

  public ngAfterContentInit(): void {
    this.initialValue = this.bomQuantity;
  }

  public writeValue(value: number): void {
    this.bomQuantityChanged(value);
  }

  public registerOnChange(method: onChanged): void {
    this.propagateChanged = method;
  }

  public registerOnTouched(method: onTouched): void {
    this.propagateTouched = method;
  }

  public bomQuantityChanged(sheets = 0): void {
    const inventoryUnit = this.isInventoryUnitDisplayed ? this.calculateInventoryQuantityFromBomQuantity(sheets) : 0;
    const height = this.isHeightDisplay ? this.calculateMmFromSheet(sheets) : 0;
    this.setSheetQuantities(sheets, inventoryUnit, height);
    if (this.isReelDiameterDisplay) {
      const outerReelDiameter = this.calculateReelDiameterFromLength(sheets, this.outerCoreDiameter, this.thickness);
      this.setReelQuantities(sheets, inventoryUnit, outerReelDiameter);
    }
  }

  public inventoryQuantityChanged(inventoryUnit = 0): void {
    const sheets = this.bomUnitFactor > 0 ? inventoryUnit * this.bomUnitFactor : 0;
    const height = this.calculateMmFromSheet(sheets);
    this.setSheetQuantities(sheets, inventoryUnit, height);
    if (this.isReelDiameterDisplay) {
      const outerReelDiameter = this.calculateReelDiameterFromLength(sheets, this.outerCoreDiameter, this.thickness);
      this.setReelQuantities(sheets, inventoryUnit, outerReelDiameter);
    }
  }

  public heightChanged(height = 0): void {
    const sheets = (height / this.thickness) * 1000;
    const inventoryUnit = this.calculateInventoryQuantityFromBomQuantity(sheets);

    this.setSheetQuantities(sheets, inventoryUnit, height);
  }

  public countReelDiameterOnCoreDiameterChange(outerCoreDiameter): void {
    this.isDiameterInputValid = outerCoreDiameter < this.outerReelDiameter;
    if (!this.isDiameterInputValid) {
      this.isDiameterInputValid = false;
      this.validationChanged.emit(false);
      this.outerCoreDiameter = outerCoreDiameter;
      return;
    }
    this.isDiameterInputValid = true;
    this.validationChanged.emit(true);
    this.outerCoreDiameter = outerCoreDiameter;

    const length = this.calculateLengthFromReelAndCoreDiameter(this.outerReelDiameter, this.outerCoreDiameter, this.thickness);
    const inventoryUnit = this.calculateInventoryQuantityFromBomQuantity(length);
    this.setReelQuantities(length, inventoryUnit, this.outerReelDiameter);
  }

  public countReelDiameterOnReelDiameterChange(outerReelDiameter): void {
    this.isDiameterInputValid = this.outerCoreDiameter < outerReelDiameter;
    if (!this.isDiameterInputValid) {
      this.isDiameterInputValid = false;
      this.validationChanged.emit(false);
      this.outerReelDiameter = outerReelDiameter;
      return;
    }
    this.isDiameterInputValid = true;
    this.validationChanged.emit(true);
    this.outerReelDiameter = outerReelDiameter;

    const length = this.calculateLengthFromReelAndCoreDiameter(this.outerReelDiameter, this.outerCoreDiameter, this.thickness);
    const inventoryUnit = this.calculateInventoryQuantityFromBomQuantity(length);
    this.setReelQuantities(length, inventoryUnit, this.outerReelDiameter);
  }

  /**
   * Calculates the length of material on a reel based on the outer reel diameter, core diameter, and material thickness.
   *
   * Formula:
   *   length = π * (outerReelDiameter² - outerCoreDiameter²) / (4 * thickness)
   *
   * @param outerReelDiameter - The outer diameter of the reel (in mm).
   * @param outerCoreDiameter - The outer diameter of the core (in mm).
   * @param thickness - The thickness of the material (in um).
   * @returns The calculated length of the material (in m).
   */
  private calculateLengthFromReelAndCoreDiameter(outerReelDiameter: number, outerCoreDiameter: number, thickness: number): number {
    return (Math.PI * (outerReelDiameter * outerReelDiameter - outerCoreDiameter * outerCoreDiameter)) / (4 * thickness);
  }

  /**
   * Calculates the outer reel diameter based on the material length, core diameter, and material thickness.
   *
   * Formula:
   *   outerReelDiameter = sqrt((4 * length * thickness) / π + outerCoreDiameter²)
   *
   * @param length - The length of the material (in m).
   * @param outerCoreDiameter - The outer diameter of the core (in mm).
   * @param thickness - The thickness of the material (in um).
   * @returns The calculated outer reel diameter (in mm).
   */
  private calculateReelDiameterFromLength(length: number, outerCoreDiameter: number, thickness: number): number {
    return Math.sqrt((4 * length * thickness) / Math.PI + outerCoreDiameter * outerCoreDiameter);
  }

  /**
   * Calculates the weight in kilograms from the number of sheets using the BOM unit factor.
   *
   * @param sheets - The number of sheets.
   * @returns The calculated weight in kilograms. Returns 0 if BOM unit factor is not greater than 0.
   */
  private calculateInventoryQuantityFromBomQuantity(sheets: number): number {
    return this.bomUnitFactor > 0 ? sheets / this.bomUnitFactor : 0;
  }

  private calculateMmFromSheet(sheets: number): number {
    return (sheets * this.thickness) / 1000;
  }

  private roundTo2DecimalPlaces(numberToRound: number): number {
    return Math.round(numberToRound * 100) / 100;
  }

  private ceil(value: number, decimalPlaces: number): number {
    if (!Number.isInteger(decimalPlaces)) {
      throw new Error('Parameter decimalPlaces must be an integer!');
    }
    const scale = Math.pow(10, decimalPlaces);
    return Math.ceil(value * scale) / scale;
  }

  private setSheetQuantities(sheets: number, kilograms: number, millimeters: number): void {
    this.bomQuantity = this.ceil(sheets, this.decimalDigitCount);
    this.formInventoryQuantity = this.roundTo2DecimalPlaces(kilograms);
    this.height = this.roundTo2DecimalPlaces(millimeters);
    this.propagateChanged(this.bomQuantity);
  }

  private setReelQuantities(sheets: number, kilograms: number, millimeters: number): void {
    this.bomQuantity = this.ceil(sheets, this.decimalDigitCount);
    this.formInventoryQuantity = this.roundTo2DecimalPlaces(kilograms);
    this.outerReelDiameter = this.roundTo2DecimalPlaces(millimeters);
    this.propagateChanged(this.bomQuantity);
  }

  public resetQuantities(): void {
    this.bomQuantityChanged(this.initialValue);
  }

  private checkInventoryUnitDisplay(): boolean {
    return (
      this.article &&
      this.article.inventoryUnitId &&
      this.article.bomUnitFactor >= 0 &&
      this.article.inventoryUnitId !== this.article.bomUnitId &&
      this.article.productType === ProductType.RawMaterial
    );
  }

  private checkHeightDisplay(): boolean {
    return (
      this.article &&
      this.article.classification === 'Sheet' &&
      this.article.thickness >= 0 &&
      this.article.productType === ProductType.RawMaterial
    );
  }

  private checkReelDisplay(): boolean {
    return (
      this.article &&
      this.article.classification === 'Reel' &&
      this.article.thickness &&
      this.article.bomUnitId === 'M' &&
      this.article.productType === ProductType.RawMaterial
    );
  }

  private setDecimalDigitCount(unitId: string): number {
    if (!CounterHelper.IsWholeUnit(unitId)) {
      return 2;
    }
    return 0;
  }
}
